import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { BehaviorSubject, from, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { environmentConfig } from '../../environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environmentConfig.apiBaseUrl;
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private timeout: any;
  private warningTimeout: any;
  private timeoutDuration: number = 15 * 60 * 1000;   // 15m (m * s * ms)
  private warningDurationMs: number = 1.5 * 60 * 1000; // 90s (m * s * ms)

  private sessionWarningSubject = new BehaviorSubject<boolean>(false);
  sessionWarning$ = this.sessionWarningSubject.asObservable();

  private warningSecondsRemainingSubject = new BehaviorSubject<number>(0);
  warningSecondsRemaining$ = this.warningSecondsRemainingSubject.asObservable();

  private countdownInterval: any;
  private activityEvents = ['click', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  private activityListenersRegistered = false;
  private sessionExpiryTime: number | null = null;
  private warningActive = false;
  private idleWatchStarted = false;
  private authStateStartSub?: Subscription;
  private activityHandler = () => this.resetUserSessionTimer();

  constructor(
    public auth: AngularFireAuth,
    private http: HttpClient,
    private router: Router
  ) {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      this.tokenSubject.next(storedToken);
    }

    this.auth.onAuthStateChanged(user => {
      if (user) {
        //console.log("AuthStateChanged",user)
        sessionStorage.setItem("wOSUser", JSON.stringify(user));
        this.startUserSessionTimer();
        this.detectUserActivity();
      } else {
        this.clearUserSessionTimer();
        this.removeUserActivityListeners();
      }
    });
  }

  public startIdleWatch(): void {
    if (this.idleWatchStarted) return;

    const immediateUser = getAuth().currentUser;
    if (immediateUser) {
      this.startIdleInternals();
      return;
    }

    this.authStateStartSub?.unsubscribe();
    this.authStateStartSub = this.auth.authState.pipe(take(1)).subscribe(user => {
      if (user) {
        this.startIdleInternals();
      }
    });

    setTimeout(() => {
      if (this.idleWatchStarted) return;
      const retryUser = getAuth().currentUser;
      if (retryUser) this.startIdleInternals();
    }, 1000);
  }

  private startIdleInternals(): void {
    this.idleWatchStarted = true;
    this.startUserSessionTimer();
    this.detectUserActivity();
  }

  public stopIdleWatch(): void {
    this.idleWatchStarted = false;

    this.authStateStartSub?.unsubscribe();
    this.authStateStartSub = undefined;

    this.clearUserSessionTimer(true);
    this.removeUserActivityListeners();
  }

  public extendSession(): void {
    this.warningActive = false;
    this.sessionWarningSubject.next(false);
    this.warningSecondsRemainingSubject.next(0);
    this.stopCountdown();

    this.startUserSessionTimer();
  }

  signInWithGoogle(): Observable<{ userCredential: any; token: string }> {
    const provider = new GoogleAuthProvider();
    provider.addScope(
      'https://www.googleapis.com/auth/calendar.readonly ' +
      'https://www.googleapis.com/auth/gmail.readonly ' +
      'https://www.googleapis.com/auth/drive.metadata.readonly ' +
      'https://www.googleapis.com/auth/drive.readonly'
    );
    provider.setCustomParameters({ prompt: 'select_account' });

    return from(signInWithPopup(getAuth(), provider)).pipe(
      map(userCredential => {
        const credential = GoogleAuthProvider.credentialFromResult(userCredential);
        const token = credential?.accessToken;
        if (!token) throw new Error('Google OAuth Token not found.');

        this.tokenSubject.next(token);
        localStorage.setItem('auth_token', token);

        getAuth().currentUser?.getIdToken(true).then(() => { });

        return { userCredential, token };
      }),
      catchError(error => {
        return throwError(() => new Error(error?.message || 'Google Auth Error'));
      })
    );
  }

  signInWithEmailPassword(email: string, password: string): Observable<void> {
    return from(this.auth.signInWithEmailAndPassword(email, password)).pipe(
      switchMap(async (cred) => {
        const idToken = await cred.user?.getIdToken(true);
        if (idToken) localStorage.setItem('auth_token', idToken);
      }),
      catchError(error => {
        return throwError(() => new Error(error?.message || 'Email/Password Auth Error'));
      })
    );
  }

  getToken(): Observable<string | null> {
    return this.tokenSubject.asObservable();
  }

  getUserRole(): Observable<number> {
    const cachedRole = sessionStorage.getItem('wOSUserRole');
    const cachedUserId = sessionStorage.getItem('wOSUserId');
    if (cachedRole && cachedUserId) {
      return of(parseInt(cachedRole, 10));
    }

    const user = getAuth().currentUser;
    const email = user?.email;
    if (!email) return of(0);

    return from(user.getIdToken()).pipe(
      switchMap(token => {
        const headers = { Authorization: `Bearer ${token}` };
        const url = `${this.apiUrl}/api/getuserrole?email=${encodeURIComponent(email)}`;
        return this.http.get<{ userId: number; role: number }>(url, { headers });
      }),
      map(res => {
        sessionStorage.setItem('wOSUserRole', String(res.role));
        sessionStorage.setItem('wOSUserId', String(res.userId));
        return res.role;
      }),
      catchError(() => of(0))
    );
  }

  getUsername(): Observable<string | null> {
    return this.auth.authState.pipe(
      map(user => (user ? user.displayName || user.email : null))
    );
  }

  logout(reason: 'manual' | 'expired' = 'manual'): Observable<void> {
    this.stopIdleWatch();

    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('wOSUser');
    sessionStorage.removeItem('wOSUserRole');
    sessionStorage.removeItem('wOSUserId');

    return from(
      this.auth.signOut().then(() => {
        this.router.navigate(['/login'], { queryParams: { reason } });
      })
    );
  }

  private startUserSessionTimer(): void {
    this.clearUserSessionTimer(false);

    this.sessionExpiryTime = Date.now() + this.timeoutDuration;

    const warningDelay = Math.max(0, this.timeoutDuration - this.warningDurationMs);

    this.warningTimeout = setTimeout(() => {
      this.warningActive = true;
      this.sessionWarningSubject.next(true);
      this.startCountdown();
    }, warningDelay);

    this.timeout = setTimeout(() => {
      this.logout('expired').subscribe();
    }, this.timeoutDuration);
  }

  private clearUserSessionTimer(forceClearWarning = true): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }

    this.stopCountdown();
    this.sessionExpiryTime = null;

    if (forceClearWarning) {
      this.warningActive = false;
      this.sessionWarningSubject.next(false);
      this.warningSecondsRemainingSubject.next(0);
    }
  }

  private detectUserActivity(): void {
    if (this.activityListenersRegistered) return;

    this.activityEvents.forEach(event =>
      window.addEventListener(event, this.activityHandler, { passive: true })
    );
    this.activityListenersRegistered = true;
  }

  private removeUserActivityListeners(): void {
    if (!this.activityListenersRegistered) return;

    this.activityEvents.forEach(event =>
      window.removeEventListener(event, this.activityHandler)
    );
    this.activityListenersRegistered = false;
  }

  clearSessionWarning(): void {
    this.sessionWarningSubject.next(false);
  }

  private async resetUserSessionTimer(): Promise<void> {
    if (this.warningActive) return;

    const currentUser = await this.auth.currentUser;
    if (currentUser) {
      this.startUserSessionTimer();
    }
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.warningSecondsRemainingSubject.next(this.getRemainingSessionSeconds());

    this.countdownInterval = setInterval(() => {
      const remaining = this.getRemainingSessionSeconds();
      this.warningSecondsRemainingSubject.next(remaining);
      if (remaining <= 0) this.stopCountdown();
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private getRemainingSessionSeconds(): number {
    if (!this.sessionExpiryTime) return 0;
    const remainingMs = this.sessionExpiryTime - Date.now();
    return Math.ceil(Math.max(0, remainingMs) / 1000);
  }

  getRemainingSessionTimeMs(): number | null {
    if (!this.sessionExpiryTime) return null;
    const remaining = this.sessionExpiryTime - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  updateLastLogin(email: string): void {
    console.log("testing updateLastLogin", email);
  }
}
